import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isFuture } from "date-fns";
import { IndianRupee, CalendarDays, CheckCircle, AlertCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CashReportingPage({ user }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ cash_amount: "", notes: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const monthStr = format(currentMonth, "yyyy-MM");
      const response = await axios.get(`${API}/cash-reports?month=${monthStr}`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const getReportForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return reports.find(r => r.date === dateStr);
  };

  const handleDateClick = (date) => {
    if (isFuture(date)) {
      toast.error("Cannot report for future dates");
      return;
    }
    
    setSelectedDate(date);
    const existing = getReportForDate(date);
    if (existing) {
      setFormData({
        cash_amount: existing.cash_amount.toString(),
        notes: existing.notes || ""
      });
    } else {
      setFormData({ cash_amount: "", notes: "" });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.cash_amount || parseFloat(formData.cash_amount) < 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/cash-reports`, {
        date: format(selectedDate, "yyyy-MM-dd"),
        cash_amount: parseFloat(formData.cash_amount),
        notes: formData.notes || null
      });
      
      toast.success("Cash report saved successfully");
      setIsDialogOpen(false);
      fetchReports();
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Failed to save report");
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Calculate stats
  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  }).filter(d => !isFuture(d));

  const reportedDays = monthDays.filter(d => getReportForDate(d));
  const unreportedDays = monthDays.filter(d => !getReportForDate(d) && !isToday(d));
  const totalCash = reports.reduce((sum, r) => sum + r.cash_amount, 0);

  // Calendar day renderer
  const renderDay = (day) => {
    const report = getReportForDate(day);
    const future = isFuture(day);
    const today = isToday(day);

    return (
      <div
        onClick={() => !future && handleDateClick(day)}
        className={`
          relative p-2 h-24 border border-border rounded-lg cursor-pointer
          transition-colors duration-200
          ${future ? 'opacity-40 cursor-not-allowed bg-muted/30' : 'hover:bg-primary/5'}
          ${report ? 'bg-green-50 border-green-200' : ''}
          ${!report && !future && !today ? 'bg-red-50/50 border-red-100' : ''}
          ${today ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
      >
        <span className={`text-sm font-semibold ${today ? 'text-primary' : ''}`}>
          {format(day, 'd')}
        </span>
        {report && (
          <div className="absolute bottom-2 left-2 right-2">
            <div className="flex items-center text-xs text-green-700 font-semibold">
              <IndianRupee className="w-3 h-3" />
              {report.cash_amount.toLocaleString('en-IN')}
            </div>
            <CheckCircle className="absolute top-0 right-0 w-4 h-4 text-green-600" />
          </div>
        )}
        {!report && !future && !today && (
          <AlertCircle className="absolute bottom-2 right-2 w-4 h-4 text-red-400" />
        )}
      </div>
    );
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const startDay = startOfMonth(currentMonth).getDay();
  const emptyDays = Array(startDay).fill(null);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} activePath="/cash-reporting" />
      
      <main className="flex-1 p-6 lg:p-8 ml-0 lg:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl text-foreground mb-2">Cash Sales Reporting</h1>
          <p className="text-muted-foreground">Record your daily cash sales</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cash This Month</p>
                  <p className="text-3xl font-heading mt-1 flex items-center">
                    <IndianRupee className="w-6 h-6" />
                    {totalCash.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-secondary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Days Reported</p>
                  <p className="text-3xl font-heading mt-1 text-green-600">{reportedDays.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Days Missing</p>
                  <p className="text-3xl font-heading mt-1 text-destructive">{unreportedDays.length}</p>
                </div>
                <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar */}
        <Card className="border-0 shadow-card" data-testid="cash-calendar">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {format(currentMonth, "MMMM yyyy")}
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="rounded-full"
                  onClick={() => navigateMonth(-1)}
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="rounded-full"
                  onClick={() => navigateMonth(1)}
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-semibold text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {emptyDays.map((_, i) => (
                    <div key={`empty-${i}`} className="h-24" />
                  ))}
                  {days.map(day => (
                    <div key={day.toISOString()}>
                      {renderDay(day)}
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-6 mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 bg-green-100 border border-green-200 rounded" />
                    <span>Reported</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 bg-red-50 border border-red-100 rounded" />
                    <span>Missing</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 bg-muted/30 border border-border rounded" />
                    <span>Future</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Report Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent data-testid="cash-report-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cash_amount">Cash Sales Amount (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="cash_amount"
                    data-testid="cash-amount-input"
                    type="number"
                    placeholder="0.00"
                    value={formData.cash_amount}
                    onChange={(e) => setFormData({ ...formData, cash_amount: e.target.value })}
                    className="pl-10 h-12 text-lg"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="cash-notes-input"
                  placeholder="Any additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="rounded-full"
                data-testid="save-cash-report-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Report"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
